/**
 * GuestSaveModal — the minimal-capture save prompt.
 *
 * Shown instead of GuestEstimateModal when the retailer's capture policy is
 * `minimal` (see src/config/capturePolicy.ts). Asks for an estimate name and an
 * email, and nothing else.
 *
 * The important difference isn't the field count — it's that this modal is
 * DISMISSIBLE and declining costs the guest nothing. Under a minimal policy the
 * detailed materials list is not gated, so "Don't save" leaves them with the full
 * estimate on screen; saving only buys them retrieval and the emailed link. That
 * is what makes the email ask defensible: it is what we deliver to, not a toll.
 *
 * Visually mirrors EstimateNameModal (the logged-in "Save your estimate" prompt)
 * so the guest and member flows feel like the same product. GuestEstimateModal is
 * deliberately untouched and still serves every `full` retailer.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Tag } from 'lucide-react';
import { useEstimator } from '../../context/EstimatorContext';
import { useAppSettings } from '../../context/AppSettingsContext';
import { usePartnerBranding } from '../../context/PartnerBrandingContext';
import { submitGuestEstimate } from '../../services/api';
import { captureUtmParams } from '../../utils/utmParams';
import { trackProjectPlannerSubmitted, SaveTrigger } from '../../utils/gtmTracking';
import { reportClientError } from '../../utils/errorReporting';
import { TURNSTILE_CONFIG } from '../../config/integrations';
import { CapturePolicy, DEFAULT_CAPTURE_BLURB_KEY } from '../../config/capturePolicy';

interface Props {
  isOpen: boolean;
  policy: CapturePolicy;
  /** Analytics only: how the prompt was opened. */
  saveTrigger?: SaveTrigger;
  onClose: () => void;
  onSuccess: (guid: string) => void;
}

export const GuestSaveModal: React.FC<Props> = ({ isOpen, policy, saveTrigger, onClose, onSuccess }) => {
  const { t } = useTranslation('results');
  const { state } = useEstimator();
  const { settings: appSettings } = useAppSettings();
  const { partner } = usePartnerBranding();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);

  const turnstileActive = TURNSTILE_CONFIG.enabled && appSettings.turnstileEnabled;

  // Seed on open. Same select-the-name touch as EstimateNameModal so the
  // pre-filled name can be typed straight over.
  useEffect(() => {
    if (!isOpen) return;
    setName(state.projectInfo?.projectName || t('save.defaultName'));
    setEmail('');
    setTurnstileToken('');
    setError(null);
    setSubmitting(false);
    // Renamed from `t`, which now belongs to useTranslation.
    const focusTimer = setTimeout(() => nameRef.current?.select(), 50);
    return () => clearTimeout(focusTimer);
  }, [isOpen, state.projectInfo?.projectName, t]);

  // Turnstile widget. A two-field form is a softer spam target than a ten-field
  // one, so the protection stays — it's invisible/managed in the common case.
  useEffect(() => {
    if (!isOpen || !turnstileActive || !turnstileRef.current) return;

    const renderWidget = () => {
      if (turnstileWidgetId.current !== null) {
        (window as any).turnstile?.reset(turnstileWidgetId.current);
        return;
      }
      if ((window as any).turnstile && turnstileRef.current) {
        turnstileWidgetId.current = (window as any).turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_CONFIG.siteKey,
          callback: (token: string) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          // A failed challenge used to vanish silently; now it's an error report.
          'error-callback': (code: string) => {
            setTurnstileToken('');
            reportClientError({
              scope: 'turnstile',
              error: new Error(`Turnstile widget error ${code}`),
              extra: { turnstile_stage: 'widget', turnstile_code: String(code), turnstile_form: 'save_modal' },
            });
            return true; // handled — keep Cloudflare from logging it as unhandled
          },
          theme: 'light',
        });
      }
    };

    if ((window as any).turnstile) {
      renderWidget();
      return;
    }
    const existing = document.querySelector('script[src*="turnstile"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.onload = () => setTimeout(renderWidget, 100);
      script.onerror = () => reportClientError({
        scope: 'turnstile',
        error: new Error('Turnstile script failed to load'),
        extra: { turnstile_stage: 'script', turnstile_form: 'save_modal' },
      });
      document.head.appendChild(script);
      return;
    }
    const checkReady = setInterval(() => {
      if ((window as any).turnstile) {
        clearInterval(checkReady);
        renderWidget();
      }
    }, 100);
    return () => clearInterval(checkReady);
  }, [isOpen, turnstileActive]);

  // Drop the widget on close so a re-open renders a fresh challenge.
  useEffect(() => {
    if (!isOpen && turnstileWidgetId.current !== null) {
      try { (window as any).turnstile?.remove(turnstileWidgetId.current); } catch { /* ignore */ }
      turnstileWidgetId.current = null;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (submitting) return;
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName) { setError(t('save.errors.name')); return; }
    if (!trimmedEmail) { setError(t('save.errors.email')); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError(t('save.errors.emailInvalid'));
      return;
    }
    if (turnstileActive && !turnstileToken) {
      setError(t('save.errors.verification'));
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const result = await submitGuestEstimate({
        // Only the fields this policy asks for. Everything else is nullable in
        // the schema and simply isn't sent — the server validates against the
        // same policy, so it won't ask for what wasn't collected.
        formData: {
          projectName: trimmedName,
          email: trimmedEmail,
          turnstileToken,
        } as any,
        estimateData: state.estimateResult!,
        wizardState: {
          calculationMode: state.calculationMode,
          projectInfo: state.projectInfo,
          wallsConfig: state.wallsConfig,
          ceilingConfig: state.ceilingConfig,
          dimensionsConfig: state.dimensionsConfig,
          applicationConfig: state.applicationConfig,
          individualWalls: state.individualWalls,
          currency: state.currency,
          completedSteps: state.completedSteps,
          currentStep: state.currentStep,
        },
        retailerId: partner?.id ?? null,
        utm: captureUtmParams(),
      });

      // Conversion event fires only on confirmed success — never on click.
      if (state.estimateResult) {
        trackProjectPlannerSubmitted(
          state.calculationMode,
          state.applicationConfig.spaceType,
          result.guid,
          state.currency,
          state.estimateResult,
          { projectName: trimmedName },
          { captureMode: 'save_modal', saveTrigger: saveTrigger ?? null },
        );
      }
      if (appSettings.pardotDebug && result.pardotDebug) {
        console.log('[Pardot debug payload]', result.pardotDebug);
      }
      onSuccess(result.guid);
    } catch (err) {
      const message = err instanceof Error ? err.message : '';
      // The server's own Turnstile rejection surfaces inline; report it too.
      if (/verification/i.test(message)) {
        reportClientError({
          scope: 'turnstile',
          error: err,
          extra: { turnstile_stage: 'server', turnstile_form: 'save_modal' },
        });
      }
      setError(message || t('save.errors.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  // A retailer's own blurb is database text and arrives already in whatever
  // language it was authored in (Phase 3 will overlay it); only our default
  // goes through i18n.
  const blurb = policy.blurb || t(DEFAULT_CAPTURE_BLURB_KEY);

  return (
    <div
      className="modal d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(58, 75, 92, 0.8)' }}
      onClick={() => { if (!submitting) onClose(); }}
    >
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
              <Tag size={18} /> {t('save.title')}
            </h5>
            <button
              type="button" className="btn-close" onClick={onClose}
              disabled={submitting} aria-label={t('auth:close')}
            />
          </div>

          <div className="modal-body">
            <div className="mb-3">
              <label htmlFor="gsm-name" className="form-label small fw-semibold">
                {t('save.nameLabel')} <span className="text-danger">*</span>
              </label>
              <input
                ref={nameRef}
                id="gsm-name"
                type="text"
                className="form-control"
                placeholder={t('save.namePlaceholder')}
                value={name}
                maxLength={255}
                disabled={submitting}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="mb-2">
              <label htmlFor="gsm-email" className="form-label small fw-semibold">
                {t('save.emailLabel')} <span className="text-danger">*</span>
              </label>
              <input
                id="gsm-email"
                type="email"
                className="form-control"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                disabled={submitting}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
              />
            </div>

            <p className="text-muted small mb-0" style={{ lineHeight: 1.5 }}>{blurb}</p>

            {turnstileActive && <div ref={turnstileRef} className="mt-3 d-flex justify-content-center" />}

            {error && <div className="alert alert-danger py-2 small mt-3 mb-0">{error}</div>}
          </div>

          <div className="modal-footer">
            {policy.allowSkip && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                {t('save.dontSave')}
              </button>
            )}
            <button
              type="button"
              className="btn btn-primary d-inline-flex align-items-center gap-2"
              onClick={handleSave}
              disabled={submitting}
            >
              {submitting && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
              {submitting ? t('save.saving') : t('save.submit')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestSaveModal;
